import { FC, useEffect, useState } from 'react';
import classNames from 'classnames';
import { Button, Modal, FormInput, Dropdown } from '@praktio/common-ui';
import { cloneDeep } from 'lodash';

import { None, Roles } from '../../utils/constants';
import { getRoleName } from '../../utils/functions';
import Table from '../common/Table/Table';
import TeamBreadcrumbLink from '../common/TeamBreadcrumbLink';

import './Roles.scss';

const RoleHeaders = [
  { Header: 'Role', accessor: 'roleName' },
  { Header: 'Team', accessor: 'team' },
  {
    Header: 'Include Subteams',
    accessor: 'includeSubteamsCheck',
  },
  { Header: '', accessor: 'options' },
];

interface IModalProps {
  show: boolean;
  teams: ITeam[];
  onSave: (data: IRole) => void;
  onHide: () => void;
  role?: IRole;
}

const RoleOptions = [
  { id: Roles.TeamManager, label: getRoleName(Roles.TeamManager) as string },
  { id: Roles.Learner, label: getRoleName(Roles.Learner) as string },
];

const AddUpdateRoleModal: FC<IModalProps> = ({ role, teams, show, onSave, onHide }) => {
  const [data, setData] = useState<IRole>(role || ({} as IRole));

  useEffect(() => {
    setData({} as IRole);
  }, []);

  useEffect(() => {
    setData(role || ({} as IRole));
  }, [role]);

  const onChange = (value: string, prop?: string) => {
    if (prop) {
      const updatedData: IRole = cloneDeep(data);
      if (prop === 'teamId' && value === None) {
        updatedData[prop] = undefined;
      }
      // Default the includeSubteams checkbox to true if switching to a TeamManager role
      else if (prop === 'role' && value === Roles.TeamManager && data.role !== value) {
        updatedData.includeSubteams = true;
        updatedData[prop] = value;
      } else {
        updatedData[prop] = value;
      }

      setData(updatedData);
    }
  };

  const teamOptions = (teams || []).map((t) => ({ id: t._id, label: t.name }));
  teamOptions.unshift({ id: None, label: 'No Team' });
  return (
    <Modal
      title={`${!role ? 'Add' : 'Update'} Role`}
      onSave={() => onSave(data)}
      onHide={onHide}
      show={show}
      saveText={!role ? 'ADD' : 'UPDATE'}
      disableSave={!data.role || (data.role === Roles.TeamManager && !data.teamId)}
    >
      {show && (
        <form>
          <Dropdown
            id="role-dropdown"
            options={RoleOptions}
            value={RoleOptions.find((o) => o.id === data.role)}
            onSelect={(id) => onChange(id as string, 'role')}
            placeholder="Select Role"
            formComponent
          />
          <Dropdown
            id="team-dropdown"
            options={teamOptions}
            value={teamOptions.find((o) => o.id === data.teamId)}
            onSelect={(id) => onChange(id as string, 'teamId')}
            placeholder="Select Team"
            formComponent
          />
          {data.role === Roles.TeamManager && (
            <FormInput
              type="switch"
              value={data?.includeSubteams}
              label="Include Subteams"
              onChange={() => onChange(!data?.includeSubteams as any, 'includeSubteams')}
            />
          )}
        </form>
      )}
    </Modal>
  );
};

interface IProps {
  roles: IRole[];
  teams: ITeam[];
  onApply: (oldRole?: IRole, newRole?: IRole) => void;
}

const UserRoles: FC<IProps> = ({ roles, teams, onApply }) => {
  const [showAddUpdateModal, setShowAddUpdateModal] = useState(false);
  const [role, setRole] = useState<IRole | undefined>();
  const processedRoles =
    roles?.map((r) => {
      const data = { ...r };
      data.roleName = getRoleName(data.role);
      if (r.teamId && teams?.length) {
        const team = teams.find((t) => t._id === r.teamId);
        if (team) {
          data.team = (
            <TeamBreadcrumbLink
              team={team}
              teams={teams}
              to={`/admin/teams/${team._id}`}
              tooltipId={`role-team-${team._id}-${r.role}`}
            />
          );
        }

        if (data.role === Roles.TeamManager) {
          data.includeSubteamsCheck = (
            <div className="include-subteams">
              <span
                className={classNames('material-icons', {
                  green: data.includeSubteams,
                  red: !data.includeSubteams,
                })}
              >
                {data.includeSubteams ? 'check_box' : 'cancel'}
              </span>
            </div>
          );
        }
      }

      data.options = (
        <div className="role-options d-flex">
          <div
            className="role-option"
            onClick={() => {
              setRole(data);
              setShowAddUpdateModal(true);
            }}
          >
            <span className="material-icons">edit</span>
          </div>
          <div
            className="role-option"
            onClick={() => {
              onApply(data);
            }}
          >
            <span className="material-icons">delete</span>
          </div>
        </div>
      );

      return data;
    }) || [];

  return (
    <div className={classNames('Roles')}>
      <div>
        <Button onClick={() => setShowAddUpdateModal(true)}>ADD</Button>
      </div>
      <Table columns={RoleHeaders} data={processedRoles} noPaging />
      <AddUpdateRoleModal
        show={showAddUpdateModal}
        role={role}
        teams={teams}
        onSave={(data) => {
          setShowAddUpdateModal(false);
          onApply(role, data);
          setRole(undefined);
        }}
        onHide={() => {
          setShowAddUpdateModal(false);
          setRole(undefined);
        }}
      />
    </div>
  );
};

export default UserRoles;
